import { useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import { getCarouselWorks } from "@/utils/media";

export default function HeroCarousel() {
  const slides = getCarouselWorks(5);
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  const go = (next: number) => {
    setIndex((next + slides.length) % slides.length);
  };

  useEffect(() => {
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [slides.length]);

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  const current = slides[index];
  const typeLabel = current.type === "movie" ? "电影" : "电视剧";

  return (
    <section
      className="relative w-full h-[480px] md:h-[600px] overflow-hidden bg-primary-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.type === "movie" ? "电影" : "电视剧"}《${slide.title}》焦点推荐背景图`}
            title={`${slide.title} - 焦点推荐`}
            className={`w-full h-full object-cover object-top ${i === index ? "animate-ken-burns" : ""}`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/30"></div>

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl animate-fade-up" key={current.id}>
          <div className="flex items-center gap-3 mb-4">
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-accent-500 text-accent-950">
              焦点推荐
            </span>
            <span className="text-xs md:text-sm text-background-100/80 font-label">
              {typeLabel} · {current.year} · {current.region}
            </span>
          </div>
          <h1 className="font-heading font-black text-3xl md:text-5xl text-background-50 leading-tight">
            {current.title}
          </h1>
          <div className="mt-4 flex items-center gap-4">
            <span className="flex items-center gap-1 text-accent-400 font-bold text-lg">
              <i className="ri-star-fill text-base"></i>
              {current.rating.toFixed(1)}
            </span>
            <div className="flex flex-wrap gap-2">
              {current.genres.slice(0, 3).map((g) => (
                <span
                  key={g}
                  className="px-2 py-0.5 rounded-full text-[11px] bg-background-50/15 text-background-50 backdrop-blur-sm"
                >
                  {g}
                </span>
              ))}
            </div>
          </div>
          <p className="mt-5 text-sm md:text-base text-background-100/85 leading-relaxed line-clamp-3 max-w-xl">
            {current.synopsis}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-accent-950 text-sm font-bold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看剧情导航
            </Link>
            <Link
              to={current.type === "movie" ? "/category/movie" : "/category/tv"}
              className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              更多{typeLabel}
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一个推荐"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 hover:bg-accent-500 hover:text-accent-950 text-background-50 backdrop-blur-sm transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一个推荐"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 hover:bg-accent-500 hover:text-accent-950 text-background-50 backdrop-blur-sm transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到${s.title}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-7 bg-accent-500" : "w-1.5 bg-background-50/50 hover:bg-background-50/80"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}